import React from 'react';
import {Page} from '@adobe/aem-react-editable-components';
import initSecContext from '../ProductPricing/data/initSecContext.js';
import ProductPricingSection from './ProductPricingSection.js';
import ProductPricingComparisonTable from '../ProductPricingComparisonTable/ProductPricingComparisonTable';
import ErrorBoundary from '../shared/ErrorBoundary.js';

/**
 * Extracts product pricing sections from cqItems, handling nested structures like tabs.
 * Similar to getChildComponents in AEM React Container but filtered for product pricing.
 */
const getProductPricingSections = (cqItemsOrder = [], cqItems = {}) => {
  const sections = [];

  cqItemsOrder.forEach((itemKey) => {
    const item = cqItems[itemKey];
    if (!item || !item[':type']) return;

    // Check if it's a product pricing section
    if (item[':type'].includes('product-pricing-section')) {
      sections.push(item);
    } else if (item[':items'] && item[':itemsOrder']) {
      // Handle nested containers (tabs, containers, etc.)
      const nestedSections = getProductPricingSections(item[':itemsOrder'], item[':items']);
      sections.push(...nestedSections);
    }
  });

  return sections;
};

/**
 * Finds a specific item type in cqItems (e.g., tabs, comparison table)
 */
const findItemByType = (cqItemsOrder = [], cqItems = {}, typeMatch) => {
  for (const itemKey of cqItemsOrder) {
    const item = cqItems[itemKey];
    if (item && item[':type'] && item[':type'].includes(typeMatch)) {
      return {key: itemKey, item};
    }
  }
  return null;
};

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      appeared: false,
    };

    const cqItems = this.props.cqItems.par?.[':items'] || this.props.cqItems || {};
    const hasComparisonTable = Boolean(cqItems['product_pricing_comp']);

    this.propsObj = {
      app: this.props.app,
      appWrapper: this.props.appWrapper,
      productId: this.props.productId,
      isLast: this.props.isLast,
      totalSections: this.props.totalSections,
      order: this.props.order,
      hasCT: hasComparisonTable,
      ctID: this.props.ctID,
      cartURl: this.props.cartUrl,
      verificationNeeded: this.props.verificationNeeded,
      EcommAPI: this.props.EcommAPI,
      store: this.props.store,
      tabId: this.props.tabId,
      tabPanelId: this.props.tabPanelId,
      tabTitle: this.props.tabTitle,
    };

    initSecContext.clearPropsContext();
    initSecContext.setPropsContext(this.propsObj);
    this.ref = React.createRef();

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (!this.state.appeared && entry.isIntersecting) {
          this.setState({
            appeared: true,
          });
        }
      });
    };

    this.observer = new IntersectionObserver(
      callback,
      {
        root: this.ref.current,
        threshold: 0.1,
      },
      this.ref.current,
    );
  }

  componentDidMount() {
    if (this.ref.current) {
      setTimeout(() => {
        this.observer.observe(this.ref.current);
      }, 1000);
    }
  }

  render() {
    // Extract cqItems and cqItemsOrder, unwrapping 'par' container if present
    let cqItems = this.props.cqItems || {};
    let cqItemsOrder = this.props.cqItemsOrder || [];
    if (this.props.cqItems?.par) {
      cqItems = this.props.cqItems.par[':items'] || {};
      cqItemsOrder = this.props.cqItems.par[':itemsOrder'] || [];
    }

    // Find tabs component if it exists
    const tabsComponent = findItemByType(cqItemsOrder, cqItems, 'tabs');

    // Get comparison table if it exists and is valid
    let compTableProps = null;
    if (this.props.isLast && cqItems['product_pricing_comp']) {
      const tableItem = cqItems['product_pricing_comp'];
      if (tableItem?.[':type']?.includes('product-pricing-comparison-table')) {
        compTableProps = tableItem;
      }
    }

    // Extract all product pricing sections (handles nested structures)
    const productSections = tabsComponent
      ? getProductPricingSections(tabsComponent.item[':itemsOrder'], tabsComponent.item[':items'])
      : getProductPricingSections(cqItemsOrder, cqItems);

    // Get the current section based on order
    const currentSection = productSections[this.props.order];

    // Update context with current section key
    if (tabsComponent && currentSection) {
      // For tabs, we need to find the original key
      const tabItems = tabsComponent.item[':items'] || {};
      const tabOrder = tabsComponent.item[':itemsOrder'] || [];
      this.propsObj.currSec = tabOrder[this.props.order] || '';
    } else {
      // For non-tabs, find the section key from cqItems
      const sectionKey = cqItemsOrder.find((key) => cqItems[key] === currentSection);
      this.propsObj.currSec = sectionKey || '';
    }
    initSecContext.setPropsContext(this.propsObj);

    // Render logic based on tabs presence and comparison table
    if (tabsComponent) {
      // Pages with Core Tabs
      if (cqItems['product_pricing_comp']) {
        // With comparison table - no lazy loading
        return (
          <>
            <ErrorBoundary>
              <ProductPricingSection
                productInfo={currentSection}
                categoryId={this.props.productId}
                hasProductPricingCompTable={true}
              />
              {compTableProps && <ProductPricingComparisonTable tableProps={compTableProps} />}
            </ErrorBoundary>
          </>
        );
      }

      // Without comparison table - with lazy loading
      return (
        <>
          <ErrorBoundary>
            <div ref={this.ref}>
              {this.state.appeared
                ? (
                    <>
                      <ProductPricingSection productInfo={currentSection} categoryId={this.props.productId} />
                      {compTableProps && <ProductPricingComparisonTable tableProps={compTableProps} />}
                    </>
                  )
                : null}
            </div>
          </ErrorBoundary>
        </>
      );
    }

    // Pages without Core Tabs - use Page component
    if (cqItems['product_pricing_comp']) {
      // With comparison table - no lazy loading
      return (
        <div className="App">
          <header className="App-header"></header>
          <Page {...this.props} />
        </div>
      );
    }

    // Without comparison table - with lazy loading
    return (
      <div className="App">
        <header className="App-header"></header>
        <div ref={this.ref}>
          {this.state.appeared ? <Page {...this.props} /> : null}
        </div>
      </div>
    );
  }
}

export default App;
